import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import { useLayoutEffect, useState } from 'react';
import classNames from '@/lib/classNames';

const PADDING_X = 8;
const PADDING_Y = 7.5;

interface CutoutPosition {
    top: number;
    bottom: number;
    left: number;
    right: number;
    width: number;
    height: number;
}

export type LocaleSelectorPopoverBackdropProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export function LocaleSelectorPopoverBackdrop({ style, className, ...props }: LocaleSelectorPopoverBackdropProps) {
    const [cutout, setCutout] = useState<CutoutPosition | null>(null);

    useLayoutEffect(() => {
        const el = document.getElementById('site-toggle-highlight');
        if (!el) {
            return;
        }

        const measure = () => {
            const rect = el.getBoundingClientRect();
            setCutout({
                top: rect.top - PADDING_Y,
                bottom: rect.bottom + PADDING_Y,
                left: rect.left - PADDING_X,
                right: rect.right + PADDING_X,
                width: rect.width + PADDING_X * 2,
                height: rect.height + PADDING_Y * 2,
            });
        };
        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    return (
        <div
            {...props}
            className={classNames('fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-overlayShow', className)}
            style={{
                clipPath: cutout ? `polygon(evenodd, ${getClipPath(cutout)})` : undefined,
                ...style,
            }}
        />
    );
}

function getClipPath({ top, bottom, left, right }: CutoutPosition) {
    return [
        '0px 0px',
        '100% 0px',
        '100% 100%',
        '0px 100%',
        `0px ${top}px`,
        `${left}px ${top}px`,
        `${left}px ${bottom}px`,
        `${right}px ${bottom}px`,
        `${right}px ${top}px`,
        `0px ${top}px`,
    ].join(', ');
}
